import {matchPath} from 'react-router-dom'
import {ERole, TNetworkRole} from '../types/user.type'

export type TPageEventProperties = {
  path: string
  pathPattern: string
  clientId?: string
  projectId?: string
  networkId?: string
  role?: ERole
}

export type TPageEventPayload = {
  eventName: string
  properties: TPageEventProperties
}

export type TResolvePageEventOptions = {
  networkId?: string | null
  networkRoles?: TNetworkRole[] | null
}

/**
 * Picks the role attached to the given networkId from the user's networkRoles
 * list. Returns undefined if the user has no role on that network or if either
 * input is missing — callers should treat that as "do not attach a role".
 */
export const resolveNetworkRole = (
  networkRoles: TNetworkRole[] | null | undefined,
  networkId: string | null | undefined,
): ERole | undefined => {
  if (!networkId || !networkRoles?.length) return undefined
  return networkRoles.find(nr => nr.networkId === networkId)?.role
}

/**
 * Resolves the Amplitude page event for a given pathname by looking up the
 * first matching route pattern in the provided map. Returns null when no
 * pattern matches (e.g. 404, intermediate layout route).
 *
 * Iteration order follows the insertion order of `routeEvents` keys, so the
 * caller controls precedence.
 *
 * When `options.networkId` is provided, the matching role from
 * `options.networkRoles` is attached so each event carries the user's role
 * for the network they're acting on (a user can hold different roles on
 * different networks — that's why this lives on the event, not the user).
 */
export const resolvePageEvent = (
  pathname: string,
  routeEvents: Record<string, string>,
  options: TResolvePageEventOptions = {},
): TPageEventPayload | null => {
  for (const pattern of Object.keys(routeEvents)) {
    const match = matchPath(pattern, pathname)
    if (!match) continue

    const properties: TPageEventProperties = {
      path: pathname,
      pathPattern: pattern,
    }
    const params = match.params as Record<string, string | undefined>
    if (params.clientId) properties.clientId = params.clientId
    if (params.networkId) properties.networkId = params.networkId

    // An explicit networkId from the caller (with the user's role) wins over the
    // URL param — it carries the role, which the route params cannot.
    if (options.networkId) {
      properties.networkId = options.networkId
      const role = resolveNetworkRole(options.networkRoles, options.networkId)
      if (role) properties.role = role
    }

    return {eventName: routeEvents[pattern], properties}
  }
  return null
}
